import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  makeStyles,
  Typography
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';
import ReplayIcon from '@material-ui/icons/Replay';
import DevicesIcon from '@material-ui/icons/Devices';
import { useDispatch, useSelector } from 'react-redux';

import { updateWalletBalance } from 'src/features/authSlice';
import { deviceType as deviceTypeList } from 'src/app/constant/deviceType';
import axiosInterceptor from 'src/app/axiosInterceptor';

const formatCurrency = amount => {
  if (amount === null || amount === undefined || amount === '') return '0 Vxu';
  return Number(amount).toLocaleString('vi-VN') + ' Vxu';
};

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    background: '#D9042F',
    color: '#fff'
  },
  closeBtn: { color: '#fff' },
  content: { padding: '20px 24px' },
  deviceChip: {
    margin: '3px',
    backgroundColor: '#fde8eb',
    color: '#D9042F',
    fontWeight: 500,
    maxWidth: 220
  },
  deviceListBox: {
    maxHeight: 140,
    overflowY: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: '8px',
    backgroundColor: '#f9fafb',
    marginBottom: 16
  },
  summaryBox: {
    background: '#fff5f5',
    borderRadius: 10,
    padding: '12px 16px',
    border: '1px solid #ffd1d1',
    marginTop: 12
  },
  qrWrapper: {
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    padding: 16,
    background: '#fff',
    boxShadow: '0 4px 14px rgba(0,0,0,0.07)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16
  },
  qrImage: {
    width: 200,
    height: 200,
    objectFit: 'contain'
  },
  infoBox: {
    background: '#f9f9f9',
    borderRadius: 8,
    padding: '12px 16px',
    border: '1px solid #eef0f2'
  },
  paymentCode: {
    fontWeight: 700,
    color: '#d32f2f',
    letterSpacing: 1
  }
}));

export default function BulkExtendModal({ open, onClose, selectedDevices = [] }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const isAdmin = useSelector(state => state.authSlice.isAdmin);
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const agencyId = useSelector(state => state.authSlice.agencyId);
  const walletBalance = useSelector(state => state.authSlice.walletBalance) ?? 0;
  const agencyLevel = Number(useSelector(state => state.authSlice.agencyLevel));

  // LOCAL state riêng để tránh ghi đè Redux store chung
  const [localPrices, setLocalPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [walletPaid, setWalletPaid] = useState(false);
  const [error, setError] = useState('');

  const targetDevice = selectedDevices[0];
  const ownerAgencyId =
    agencyId ||
    targetDevice?.agency?.id ||
    targetDevice?.agency_id ||
    (typeof targetDevice?.agency === 'number' ? targetDevice.agency : undefined);

  const targetDeviceTypeName =
    targetDevice?.deviceType?.name ||
    targetDevice?.device_type_name ||
    (typeof targetDevice?.device_type === 'string' ? targetDevice.device_type : undefined);

  const foundDeviceType = deviceTypeList.find(
    t => t.name && targetDeviceTypeName && t.name.toLowerCase() === targetDeviceTypeName.toLowerCase()
  );

  const targetDeviceTypeId =
    targetDevice?.deviceType?.id ||
    targetDevice?.device_type?.id ||
    targetDevice?.device_type_id ||
    (typeof targetDevice?.device_type === 'number' ? targetDevice.device_type : undefined) ||
    foundDeviceType?.id;

  useEffect(() => {
    if (!open) return;
    setSelectedPackage(null);
    setQrData(null);
    setWalletPaid(false);
    setError('');

    const fetchSystemPackages = () => {
      setLoadingPrices(true);
      axiosInterceptor
        .get(`/service_package/packages?device_type_id=${targetDeviceTypeId || ''}&version=${targetDevice?.hardware_version || ''}`)
        .then(res => {
          const data =
            res.data?.payload?.result ||
            res.data?.payload?.data ||
            res.data?.payload?.service_packages ||
            res.data?.payload?.servicePackages ||
            [];
          setLocalPrices(data);
        })
        .catch(() => setLocalPrices([]))
        .finally(() => setLoadingPrices(false));
    };

    if (ownerAgencyId) {
      setLoadingPrices(true);
      axiosInterceptor
        .get(`/service_package_price?owner_type=agency&owner_id=${ownerAgencyId}&page=1&page_size=100`)
        .then(res => {
          const data =
            res.data?.payload?.result ||
            res.data?.payload?.data ||
            res.data?.payload?.service_package_prices ||
            res.data?.payload?.servicePackagePrices ||
            [];
          if (data.length === 0) {
            // Fallback nếu đại lý chưa cấu hình gói
            fetchSystemPackages();
          } else {
            setLocalPrices(data);
          }
        })
        .catch(() => {
          fetchSystemPackages();
        })
        .finally(() => setLoadingPrices(false));
    } else {
      fetchSystemPackages();
    }
  }, [open, ownerAgencyId, targetDeviceTypeId, targetDevice]);

  const customPricesForDeviceType = (localPrices || []).filter(item => {
    const pkg = item.service_package || item.servicePackage || item;
    const pkgName = pkg?.name || '';
    const isActivation = pkg?.type ? pkg.type === 'KICH_HOAT' : /kích hoạt|active|kh/i.test(pkgName);
    if (isActivation) return false;

    const pkgDeviceTypeName = pkg?.deviceType?.name || pkg?.device_type?.name || pkg?.device_type_name;
    const deviceTypeId =
      pkg?.device_type_id ||
      pkg?.device_type?.id ||
      pkg?.deviceType?.id ||
      (typeof pkg?.device_type === 'number' ? pkg.device_type : undefined) ||
      (typeof pkg?.deviceType === 'number' ? pkg.deviceType : undefined);

    // Gói áp dụng cho tất cả loại thiết bị (không quy định loại thiết bị cụ thể)
    if (!pkgDeviceTypeName && (!deviceTypeId || Number(deviceTypeId) === 0)) {
      return true;
    }

    // 1. So khớp theo tên loại thiết bị
    if (pkgDeviceTypeName && targetDeviceTypeName && pkgDeviceTypeName.toLowerCase() === targetDeviceTypeName.toLowerCase()) {
      return true;
    }

    // 2. Fallback so khớp theo ID
    if (deviceTypeId && targetDeviceTypeId && Number(deviceTypeId) === Number(targetDeviceTypeId)) {
      return true;
    }

    return false;
  });

  // Build package options
  const packageOptions = customPricesForDeviceType.map(item => {
    const pkg = item.service_package || item.servicePackage || item;
    const price = item.price ?? pkg.cost;
    return {
      priceRecordId: item.id,          // service_package_price id
      packageId: Number(pkg.id),
      name: `${pkg.name} — ${formatCurrency(price)}`,
      rawName: pkg.name,
      price,
      deviceTypeName: pkg.device_type?.name || pkg.device_type_id
    };
  });

  const totalAmount =
    selectedPackage ? selectedPackage.price * selectedDevices.length : 0;

  const isFree = totalAmount === 0;
  const hasEnoughBalance = isFree || isAdmin || (agencyLevel === 1 && walletBalance >= totalAmount);
  const shortfall = (!isFree && totalAmount > walletBalance) ? totalAmount - walletBalance : 0;

  const handlePayment = async () => {
    if (!selectedPackage) {
      setError('Vui lòng chọn gói dịch vụ gia hạn');
      return;
    }
    const deviceIds = selectedDevices.map(d => d.id);

    setLoading(true);
    setError('');
    try {
      if (hasEnoughBalance) {
        // Pay directly from wallet
        const response = await axiosInterceptor.post('/QRCode/renew-from-wallet', {
          service_package_price_id: selectedPackage.priceRecordId,
          device_ids: deviceIds
        });

        if (response.data?.success) {
          setWalletPaid(true);
          const walletBal = response.data?.payload?.result?.wallet_balance;
          if (walletBal !== undefined && walletBal !== null) {
            dispatch(updateWalletBalance(walletBal));
          }
        } else {
          setError(response.data?.message || 'Không thể thanh toán bằng Vxu. Vui lòng thử lại.');
        }
      } else {
        // Not enough balance → generate QR
        let response;
        if (isAdmin) {
          response = await axiosInterceptor.post('/QRCode/create/admin', {
            owner_type: 'agency',
            owner_id: ownerAgencyId,
            service_package_price_id: selectedPackage.priceRecordId,
            device_ids: deviceIds
          });
        } else {
          response = await axiosInterceptor.post('/QRCode/create/agency', {
            service_package_price_id: selectedPackage.priceRecordId,
            device_ids: deviceIds
          });
        }

        if (response.data?.success) {
          setQrData(response.data?.payload?.result);
        } else {
          setError(response.data?.message || 'Không thể tạo mã QR. Vui lòng thử lại.');
        }
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        'Có lỗi xảy ra khi xử lý thanh toán.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPackage(null);
    setQrData(null);
    setWalletPaid(false);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gridGap={8}>
          <Typography variant="h6" style={{ fontWeight: 700, color: '#fff' }}>
            {selectedDevices.length === 1
              ? 'Gia hạn thiết bị'
              : `Gia hạn hàng loạt (${selectedDevices.length} thiết bị)`}
          </Typography>
        </Box>
        <IconButton className={classes.closeBtn} onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.content}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={5}>
            <CircularProgress size={40} style={{ color: '#D9042F' }} />
            <Typography variant="body2" style={{ marginTop: 16, color: '#666' }}>
              {hasEnoughBalance ? 'Đang xử lý thanh toán bằng Vxu...' : 'Đang tạo mã QR thanh toán...'}
            </Typography>
          </Box>
        ) : walletPaid ? (
          /* ── Wallet Success ── */
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <Box
              style={{
                width: 72, height: 72, borderRadius: '50%',
                background: 'linear-gradient(135deg, #D9042F 0%, #ff4d6d 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 16, boxShadow: '0 4px 20px rgba(217,4,47,0.3)'
              }}
            >
              <Typography style={{ color: '#fff', fontSize: 32 }}>✓</Typography>
            </Box>
            <Typography variant="h6" style={{ fontWeight: 700, color: '#D9042F', marginBottom: 8 }}>
              Gia hạn thành công!
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              Đã gia hạn <b>{selectedDevices.length}</b> thiết bị bằng Vxu.
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8, color: '#666' }}>
              Gói: <b>{selectedPackage?.rawName}</b> · Tổng: <b style={{ color: '#D9042F' }}>{formatCurrency(totalAmount)}</b>
            </Typography>
            <Typography variant="body2" style={{ marginTop: 4, color: '#2e7d32', fontWeight: 600 }}>
              Số dư Vxu mới: {formatCurrency(walletBalance)}
            </Typography>
          </Box>
        ) : qrData ? (
          /* ── QR Result ── */
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              style={{ marginBottom: 12 }}
            >
              Quét mã QR bằng ứng dụng ngân hàng để thanh toán gia hạn{' '}
              <b>{selectedDevices.length}</b> thiết bị.
            </Typography>

            {qrData.qrCodeBase64 && (
              <Box className={classes.qrWrapper}>
                <img
                  src={
                    qrData.qrCodeBase64.startsWith('data:')
                      ? qrData.qrCodeBase64
                      : `data:image/png;base64,${qrData.qrCodeBase64}`
                  }
                  alt="QR Code gia hạn"
                  className={classes.qrImage}
                />
              </Box>
            )}

            <Box width="100%" className={classes.infoBox}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">
                  Gói dịch vụ:
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {selectedPackage?.rawName}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">
                  Số thiết bị:
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {selectedDevices.length} thiết bị
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">
                  Tổng tiền:
                </Typography>
                <Typography
                  variant="body2"
                  style={{ fontWeight: 700, color: '#2e7d32', fontSize: 15 }}
                >
                  {formatCurrency(qrData.amount ?? totalAmount)}
                </Typography>
              </Box>
              <Divider style={{ margin: '8px 0' }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  Nội dung chuyển khoản:
                </Typography>
                <Typography className={classes.paymentCode} variant="body2">
                  {qrData.payment_code}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                align="center"
                style={{ marginTop: 8, fontStyle: 'italic' }}
              >
                * Ghi đúng nội dung chuyển khoản để hệ thống tự động gia hạn.
              </Typography>
            </Box>
          </Box>
        ) : (
          /* ── Form ── */
          <Box>
            {/* Device list */}
            {selectedDevices.length > 1 ? (
              <>
                <Box display="flex" alignItems="center" mb={1} gridGap={6}>
                  <DevicesIcon fontSize="small" style={{ color: '#D9042F' }} />
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Danh sách thiết bị được chọn
                  </Typography>
                </Box>
                <Box className={classes.deviceListBox}>
                  {selectedDevices.map(d => (
                    <Chip
                      key={d.id}
                      size="small"
                      className={classes.deviceChip}
                      avatar={<Avatar style={{ width: 20, height: 20, fontSize: 10, background: '#D9042F', color: '#fff' }}>{d.deviceType?.name?.[0] || 'D'}</Avatar>}
                      label={
                        d.vehicle?.license_plate
                          ? `${d.vehicle.license_plate} · ${d.serial}`
                          : d.serial || `ID ${d.id}`
                      }
                    />
                  ))}
                </Box>
              </>
            ) : (
              <Box display="flex" alignItems="center" mb={2} gridGap={6} style={{ background: '#f5f5f5', padding: '10px 14px', borderRadius: '8px' }}>
                <DevicesIcon fontSize="small" style={{ color: '#D9042F' }} />
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  Thiết bị gia hạn:{' '}
                  <span style={{ fontWeight: 700, color: '#D9042F' }}>
                    {selectedDevices[0]?.vehicle?.license_plate
                      ? `${selectedDevices[0].vehicle.license_plate} · ${selectedDevices[0].serial}`
                      : selectedDevices[0]?.serial || `ID ${selectedDevices[0]?.id}`}
                  </span>
                </Typography>
              </Box>
            )}

            {/* Package selector */}
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
              Chọn gói dịch vụ gia hạn
            </Typography>

            {loadingPrices ? (
              <Box display="flex" alignItems="center" gridGap={8} mb={2}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="textSecondary">
                  Đang tải danh sách gói cước...
                </Typography>
              </Box>
            ) : packageOptions.length === 0 ? (
              <Typography
                variant="body2"
                color="error"
                style={{ marginBottom: 12 }}
              >
                Chưa có gói cước được cấu hình cho đại lý này. Vui lòng vào{' '}
                <b>Quản trị đại lý → Cấu hình gói cước</b> để thiết lập.
              </Typography>
            ) : (
              <Autocomplete
                options={packageOptions}
                getOptionLabel={opt => opt.name}
                value={selectedPackage}
                onChange={(_, val) => {
                  setSelectedPackage(val);
                  setError('');
                }}
                disableClearable={false}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Chọn gói dịch vụ..."
                    error={!!error}
                    helperText={error}
                  />
                )}
                style={{ marginBottom: 4 }}
              />
            )}

            {/* Summary */}
            {selectedPackage && (
              <Box className={classes.summaryBox}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" color="textSecondary">
                    Đơn giá / thiết bị:
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {formatCurrency(selectedPackage.price)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" color="textSecondary">
                    Số thiết bị:
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    × {selectedDevices.length}
                  </Typography>
                </Box>
                <Divider style={{ margin: '6px 0' }} />
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" style={{ fontWeight: 700 }}>
                    Tổng Vxu:
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 700, color: '#D9042F', fontSize: 15 }}
                  >
                    {formatCurrency(totalAmount)}
                  </Typography>
                </Box>
                {agencyLevel === 1 && (
                  <>
                    <Divider style={{ margin: '6px 0' }} />
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="textSecondary">
                        Số dư Vxu:
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ fontWeight: 600, color: hasEnoughBalance ? '#2e7d32' : '#c62828' }}
                      >
                        {formatCurrency(walletBalance)}
                      </Typography>
                    </Box>
                    {!hasEnoughBalance && shortfall > 0 && (
                      <Box
                        display="flex" justifyContent="space-between" alignItems="center"
                        style={{
                          background: '#fff3e0', borderRadius: 6, padding: '6px 10px',
                          marginTop: 4
                        }}
                      >
                        <Typography variant="caption" style={{ color: '#e65100' }}>
                          ⚠ Thiếu {formatCurrency(shortfall)} — Quét QR để nạp Vxu
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '12px 24px' }}>
        {walletPaid ? (
          <Button
            variant="contained"
            onClick={handleClose}
            fullWidth
            style={{ textTransform: 'none', backgroundColor: '#D9042F', color: '#fff' }}
          >
            Đóng
          </Button>
        ) : qrData ? (
          <Button
            variant="outlined"
            onClick={() => setQrData(null)}
            fullWidth
            style={{ textTransform: 'none' }}
          >
            ← Chọn gói khác
          </Button>
        ) : (
          <>
            <Button
              onClick={handleClose}
              style={{ textTransform: 'none', color: '#666' }}
            >
              Hủy
            </Button>
            <Button
              variant="contained"
              onClick={handlePayment}
              disabled={loading || !selectedPackage || packageOptions.length === 0}
              style={{
                textTransform: 'none',
                minWidth: 200,
                backgroundColor: (loading || !selectedPackage || packageOptions.length === 0) ? undefined : '#D9042F',
                color: (loading || !selectedPackage || packageOptions.length === 0) ? undefined : '#fff'
              }}
            >
              {hasEnoughBalance && selectedPackage
                ? isFree
                  ? '💳 Gia hạn ngay (0 Vxu)'
                  : `💳 Thanh toán bằng Vxu (${formatCurrency(totalAmount)})`
                : 'Tạo QR thanh toán'
              }
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
